// src/components/PinScreen.jsx
import { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { usePin } from '@/contexts/PinContext';

const TYPE_CONFIG = {
  pin: {
    label: 'PIN', inputMode: 'numeric', minLen: 4, maxLen: 4,
    placeholder: '····',
    subtitles: { enter: null, setup: 'Choose a 4-digit PIN', confirm: 'Confirm your PIN' },
  },
  word: {
    label: 'Password', inputMode: 'text', minLen: 4, maxLen: 64,
    placeholder: 'Enter passphrase…',
    subtitles: {
      enter: null,
      setup: 'Choose a passphrase (min 4 characters)',
      confirm: 'Enter your passphrase again',
    },
  },
};

const KEYS = [['1','2','3'],['4','5','6'],['7','8','9'],['','0','⌫']];

function Numpad({ onPress, onBackspace, disabled }) {
  return (
    <div style={s.keypad}>
      {KEYS.map((row, r) => (
        <div key={r} style={s.row}>
          {row.map((key, k) => (
            <button key={k}
              style={{
                ...s.key,
                opacity: key === '' ? 0 : 1,
                cursor: key === '' ? 'default' : 'pointer',
                pointerEvents: key === '' ? 'none' : 'auto',
                background: key === '⌫' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.12)',
                color: key === '⌫' ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.95)',
                fontSize: key === '⌫' ? '1.2rem' : '1.5rem',
                border: '1px solid rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
              }}
              aria-label={key === '⌫' ? 'Backspace' : key === '' ? undefined : `Digit ${key}`}
              onClick={() => { if (key === '⌫') onBackspace(); else if (key) onPress(key); }}
              disabled={key === '' || disabled}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.91)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={e => e.currentTarget.style.transform = 'scale(0.91)'}
              onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
            >{key}</button>
          ))}
        </div>
      ))}
    </div>
  );
}

function PinDots({ length, filled }) {
  return (
    <div style={s.dots} aria-label={`${filled} of ${length} digits entered`} aria-live="polite">
      {Array(length).fill(null).map((_, i) => (
        <div key={i} aria-hidden="true" style={{
          ...s.dot,
          background: i < filled ? '#ffffff' : 'transparent',
          border: i < filled ? '2px solid #ffffff' : '2px solid rgba(255,255,255,0.35)',
          transform: i < filled ? 'scale(1.2)' : 'scale(1)',
          boxShadow: i < filled ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
        }} />
      ))}
    </div>
  );
}

// FIX: added id, name, and associated sr-only label to the input element
function TextInput({ id, name, label, value, onChange, onSubmit, placeholder, inputMode, autoFocus, showToggle, show, onToggle }) {
  const srOnly = {
    position: 'absolute', width: '1px', height: '1px',
    padding: 0, margin: '-1px', overflow: 'hidden',
    clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0,
  };
  return (
    <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
      <label htmlFor={id} style={srOnly}>{label}</label>
      <input
        id={id}
        name={name}
        type={show ? 'text' : 'password'}
        inputMode={inputMode}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onSubmit?.()}
        autoFocus={autoFocus}
        autoComplete="current-password"
        placeholder={placeholder}
        style={{
          width: '100%', padding: '14px 44px 14px 18px', borderRadius: 16,
          border: '1.5px solid rgba(255,255,255,0.25)', fontSize: '1rem',
          fontFamily: 'inherit', background: 'rgba(255,255,255,0.12)', color: '#ffffff',
          outline: 'none', boxSizing: 'border-box',
          letterSpacing: show ? 'normal' : '0.2em',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          caretColor: '#ffffff',
        }}
      />
      <style>{`input::placeholder { color: rgba(255,255,255,0.4); }`}</style>
      {showToggle && (
        <button type="button" onClick={onToggle}
          aria-label={show ? 'Hide passphrase' : 'Show passphrase'}
          style={{
            position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center',
          }}
        >
          {show ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
        </button>
      )}
    </div>
  );
}

export default function PinScreen() {
  const { hasPin, setupPin, verifyPin, unlock, lockType } = usePin();
  const cfg       = TYPE_CONFIG[lockType] || TYPE_CONFIG.pin;
  const isPinType = lockType === 'pin';

  const [step,        setStep]        = useState(hasPin ? 'enter' : 'setup');
  const [firstPin,    setFirstPin]    = useState('');
  const [inputPin,    setInputPin]    = useState('');
  const [error,       setError]       = useState('');
  const [shake,       setShake]       = useState(false);
  const [attempts,    setAttempts]    = useState(0);
  // FIX: seed from localStorage so a page refresh cannot bypass the lockout.
  // Key is ds_pin_locked_until (epoch ms string). Cleared on successful unlock.
  const [lockedUntil, setLockedUntilState] = useState(() => {
    const stored = localStorage.getItem('ds_pin_locked_until');
    if (!stored) return null;
    const ts = parseInt(stored, 10);
    return (ts && ts > Date.now()) ? ts : null;
  });

  // Wrapper so every write also persists to localStorage
  const setLockedUntil = (ts) => {
    setLockedUntilState(ts);
    if (ts) localStorage.setItem('ds_pin_locked_until', String(ts));
    else     localStorage.removeItem('ds_pin_locked_until');
  };
  const [checking,    setChecking]    = useState(false);
  const [showText,    setShowText]    = useState(false);

  const autoVerifyTimer = useRef(null);
  const isVerifying     = useRef(false);

  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 500); };

  const submit = async (value) => {
    const pin = value ?? inputPin;
    if (!pin || pin.length < cfg.minLen) return;
    if (isVerifying.current) return;
    isVerifying.current = true;
    setChecking(true);

    if (step === 'enter') {
      if (lockedUntil && Date.now() < lockedUntil) {
        const secsLeft = Math.ceil((lockedUntil - Date.now()) / 1000);
        setError(`Too many attempts. Try again in ${secsLeft}s.`);
        setInputPin(''); triggerShake();
        setChecking(false); isVerifying.current = false; return;
      }
      const ok = await verifyPin(pin);
      if (!ok) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= 5) {
          setLockedUntil(Date.now() + 30_000);
          setAttempts(0);
          setError('Too many attempts. Locked for 30 seconds.');
        } else {
          setError(`Incorrect. Try again. (${newAttempts}/5)`);
        }
        setInputPin(''); triggerShake();
      } else {
        setAttempts(0); setLockedUntil(null); // also clears localStorage via wrapper
        unlock();
      }
    } else if (step === 'setup') {
      setFirstPin(pin); setInputPin(''); setStep('confirm');
    } else if (step === 'confirm') {
      if (pin === firstPin) {
        await setupPin(pin);
      } else {
        setError('Does not match. Start over.');
        setInputPin(''); setFirstPin(''); setStep('setup'); triggerShake();
      }
    }

    setChecking(false);
    isVerifying.current = false;
  };

  useEffect(() => {
    if (isPinType) return;
    if (step !== 'enter') return;
    if (inputPin.length < cfg.minLen) return;
    clearTimeout(autoVerifyTimer.current);
    autoVerifyTimer.current = setTimeout(() => { submit(inputPin); }, 400);
    return () => clearTimeout(autoVerifyTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputPin, step, isPinType]);

  const handleNumpadPress = async (digit) => {
    if (checking || inputPin.length >= cfg.maxLen) return;
    const next = inputPin + digit;
    setInputPin(next); setError('');
    if (next.length >= cfg.maxLen) await submit(next);
  };

  const handleBackspace  = () => { setInputPin(prev => prev.slice(0, -1)); setError(''); };
  const handleTextChange = (v) => { setInputPin(v); setError(''); };

  const stepLabel = { enter: 'Welcome back', setup: 'Create your lock', confirm: 'Confirm your lock' };

  // Derive a meaningful label for the text input based on current step
  const textInputLabel = {
    enter:   'Enter your passphrase',
    setup:   'Choose a passphrase (min 4 characters)',
    confirm: 'Confirm your passphrase',
  }[step] || 'Passphrase';

  const textInputId = `pinscreen-passphrase-${step}`;

  return (
    <div style={s.container}>
      <div style={s.blob1} /><div style={s.blob2} /><div style={s.blob3} /><div style={s.grain} />

      <div style={{ ...s.inner, animation: shake ? 'shake 0.5s' : 'fadeUp 0.5s ease both' }}>

        <div style={s.logoWrap}>
          <img src="/icons/icon-192.png" alt="Sheepheard" style={s.logoImg}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          <div style={{ ...s.logoFallback, display: 'none' }}>🐑</div>
        </div>

        <h1 style={s.appName}>Sheepheard</h1>
        <p style={s.tagline}>"My sheep hear my voice" — John 10:27</p>

        <p style={s.stepLabel}>{stepLabel[step]}</p>

        {cfg.subtitles[step] && <p style={s.subtitle}>{cfg.subtitles[step]}</p>}

        {isPinType && <PinDots length={cfg.maxLen} filled={inputPin.length} />}

        {!isPinType && (
          <div style={{ margin: '1.25rem 0 0', width: '100%', maxWidth: 300 }}>
            {/* FIX: pass id, name, and label to TextInput */}
            <TextInput
              id={textInputId}
              name="pinscreen-passphrase"
              label={textInputLabel}
              value={inputPin}
              onChange={handleTextChange}
              onSubmit={() => submit()}
              placeholder={cfg.placeholder}
              inputMode={cfg.inputMode}
              autoFocus
              showToggle
              show={showText}
              onToggle={() => setShowText(v => !v)}
            />
            {checking && step === 'enter' && (
              <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                Checking…
              </p>
            )}
          </div>
        )}

        <div style={s.errorWrap}>
          {error && <p style={s.error} role="alert">⚠ {error}</p>}
        </div>

        {isPinType ? (
          <Numpad onPress={handleNumpadPress} onBackspace={handleBackspace} disabled={checking} />
        ) : (
          step !== 'enter' && (
            <button
              onClick={() => submit()}
              disabled={checking || inputPin.length < cfg.minLen}
              style={{
                width: '100%', maxWidth: 300, padding: '14px', borderRadius: 50,
                background: inputPin.length >= cfg.minLen ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.2)',
                color: inputPin.length >= cfg.minLen ? '#14532d' : 'rgba(255,255,255,0.4)',
                fontWeight: 700, fontSize: '1rem',
                border: '1px solid rgba(255,255,255,0.3)',
                cursor: inputPin.length >= cfg.minLen ? 'pointer' : 'default',
                transition: 'all 0.2s', fontFamily: 'inherit', backdropFilter: 'blur(8px)',
              }}
            >
              {checking ? '…' : step === 'setup' ? 'Continue' : 'Confirm'}
            </button>
          )
        )}

        <p style={s.hint}>
          {step === 'setup'
            ? `🔒 Your ${cfg.label.toLowerCase()} encrypts all data locally`
            : '🌿 "The Lord is my Shepherd, that\'s all I want!"'}
        </p>

        {/* FIX: Forgot-PIN guidance — only shown on 'enter' step */}
        {step === 'enter' && (
          <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', marginTop: '0.75rem', textAlign: 'center', lineHeight: 1.6 }}>
            Forgot your {cfg.label.toLowerCase()}?{' '}
            Go to <strong style={{ color: 'rgba(255,255,255,0.4)' }}>Settings → Security</strong> after signing in,
            or sign out and sign back in to remove it.
          </p>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%,100%{transform:translateX(0)} 20%{transform:translateX(-8px)}
          40%{transform:translateX(8px)} 60%{transform:translateX(-8px)} 80%{transform:translateX(8px)}
        }
        @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

const s = {
  container: {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0f3d22 0%, #1a1208 55%, #2d1a0a 100%)',
    padding: '1rem', position: 'relative', overflow: 'hidden',
  },
  blob1: {
    position: 'absolute', top: '-120px', right: '-100px',
    width: '450px', height: '450px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(77,124,15,0.35) 0%, transparent 65%)',
    pointerEvents: 'none', filter: 'blur(40px)',
  },
  blob2: {
    position: 'absolute', bottom: '-100px', left: '-80px',
    width: '380px', height: '380px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(180,83,9,0.25) 0%, transparent 65%)',
    pointerEvents: 'none', filter: 'blur(40px)',
  },
  blob3: {
    position: 'absolute', top: '40%', left: '20%',
    width: '300px', height: '300px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(21,128,61,0.12) 0%, transparent 65%)',
    pointerEvents: 'none', filter: 'blur(60px)',
  },
  grain: {
    position: 'absolute', inset: 0,
    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
    pointerEvents: 'none', opacity: 0.5,
  },
  inner: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    width: '100%', maxWidth: '340px',
    textAlign: 'center', position: 'relative', zIndex: 1,
  },
  logoWrap: {
    width: 80, height: 80, borderRadius: 24, overflow: 'hidden',
    marginBottom: '1rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(10px)',
  },
  logoImg:      { width: '100%', height: '100%', objectFit: 'cover' },
  logoFallback: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' },
  appName: {
    fontSize: '1.6rem', fontWeight: '800', margin: '0 0 0.2rem',
    color: '#ffffff', fontFamily: 'Georgia, serif', letterSpacing: '-0.01em',
    textShadow: '0 2px 12px rgba(0,0,0,0.3)',
  },
  tagline: {
    fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', margin: '0 0 1.75rem',
    textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600',
  },
  stepLabel: {
    fontSize: '1rem', fontWeight: '600', color: 'rgba(255,255,255,0.85)',
    margin: '0 0 0.35rem', fontFamily: 'Georgia, serif',
  },
  subtitle: { fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', margin: '0 0 1rem', lineHeight: 1.5 },
  dots: { display: 'flex', justifyContent: 'center', gap: '16px', margin: '1.25rem 0 0.25rem' },
  dot: { width: '13px', height: '13px', borderRadius: '50%', transition: 'all 0.18s cubic-bezier(0.34,1.56,0.64,1)' },
  errorWrap: { minHeight: '1.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0.5rem 0' },
  error: {
    color: '#fca5a5', fontSize: '0.78rem', margin: 0,
    background: 'rgba(220,38,38,0.2)', padding: '0.3rem 0.85rem',
    borderRadius: '999px', border: '1px solid rgba(252,165,165,0.3)',
    fontWeight: '500', backdropFilter: 'blur(8px)',
  },
  keypad: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '0.5rem', width: '100%' },
  row:    { display: 'flex', justifyContent: 'center', gap: '16px' },
  key: {
    width: '76px', height: '76px', borderRadius: '50%', fontWeight: '400',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'transform 0.1s, background 0.15s',
    WebkitTapHighlightColor: 'transparent', userSelect: 'none',
  },
  hint: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '1.5rem', lineHeight: 1.5, fontStyle: 'italic' },
};